import React from 'react'
import { AssessmentWithRelations, PermissionRole } from 'silta-ai-backend'
import styled from 'styled-components'
import cx from 'classnames'
import { useAnswerReferences } from 'utils/answers'
import { downloadDocument } from 'utils/download'
import { ReferenceMatch } from 'utils/references'
import { themeVariables } from '../themes/themeVariables'
import { Tooltip } from './Tooltip'
import { FileTypeIcon } from './FileTypeIcon'
import { Icon } from './Icon'

interface AnswerContentProps {
    answer: AssessmentWithRelations['answers'][0]
    permissionsToDataRooms: Record<string, PermissionRole | null>
}

const ReferenceLink = styled.a`
    color: ${themeVariables.colors.secondary};
    text-decoration: none;
    font-size: 10px;
    padding: 1px 2px;
    border: 1px solid ${themeVariables.palettes.neutral400};
    border-radius: 2px;
    &:hover {
        color: ${themeVariables.colors.primary};
    }
`

export function AnswerContent({
    answer,
    permissionsToDataRooms,
}: AnswerContentProps) {
    const { paragraphs } = useAnswerReferences({
        answer,
        referenceRenderer: (match: ReferenceMatch) => {
            if (!match.reference.document && !match.reference.url) {
                return '[invalid reference]'
            }

            const doc = match.reference.document

            return (
                <sup>
                    <Tooltip
                        anchorDisplay="inline"
                        content={
                            <span className={cx('d-flex', 'g-5')}>
                                <FileTypeIcon
                                    fileName={match.reference.title}
                                />
                                <span>
                                    {match.reference.title}
                                    <Icon
                                        name="exportArrow"
                                        style={{
                                            display: 'inline',
                                            marginLeft: '5px',
                                        }}
                                    />
                                </span>
                            </span>
                        }
                    >
                        {doc && (
                            <ReferenceLink
                                href=""
                                onClick={(e) => {
                                    e.preventDefault()
                                    if (
                                        permissionsToDataRooms[doc.dataRoomId]
                                    ) {
                                        downloadDocument(doc)
                                    } else {
                                        // TODO: Show a notification saying the user does not have access to this document - AI-372
                                        // For now leaving a alert
                                        // eslint-disable-next-line no-alert
                                        alert(
                                            'You do not have access to this document.'
                                        )
                                    }
                                }}
                            >
                                Ref. {match.reference.index}
                                {match.sectionDescription
                                    ? `, ${match.sectionDescription}`
                                    : ''}
                            </ReferenceLink>
                        )}
                        {match.reference.url && (
                            <ReferenceLink
                                href={match.reference.url}
                                target="_blank"
                            >
                                Ref. {match.reference.index}
                                {match.sectionDescription
                                    ? `, ${match.sectionDescription}`
                                    : ''}
                            </ReferenceLink>
                        )}
                    </Tooltip>
                </sup>
            )
        },
    })

    return (
        <>
            {paragraphs.map((paragraph, index) => (
                <div className="m-b-10" key={`Answer-${answer.id}-${index}`}>
                    {paragraph}
                </div>
            ))}
        </>
    )
}
